import { useEffect, useState } from "react";
import { Shield, Lock, Eye, Users, Globe, Mail, Phone, ArrowLeft, FileText, CreditCard, Settings} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../../context/ThemeContext";

const PrivacyPolicy = () => {
  // Replace these with your actual hooks
  const { isDarkTheme } = useTheme();
  const navigate = useNavigate();
  
  // Simulating theme context - replace with your actual useTheme hook
  const [activeSection, setActiveSection] = useState("privacy");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackClick = () => {
    navigate(-1);
  };

  const privacySections = [
    {
      id: "introduction",
      title: "Introduction",
      icon: Shield,
      content: "At OG-Fleet, we are committed to respecting and protecting the privacy of our users. This Privacy Policy explains how we collect, use, store, and share personal information when you access or interact with our platform, which is available at https://og-gpt.com. By using OG-Fleet, you consent to the collection and use of your information as described in this policy."
    },
    {
      id: "information-collection",
      title: "Information We Collect",
      icon: Eye,
      content: "When you register for an account or use our services, we may collect personal details such as your name, email address, organization name, region, and billing information, particularly when purchasing subscriptions or accessing premium features. We also automatically collect technical information including your device type, browser version, IP address, location data, the pages you visit on our site, and how you interact with our AI tools. In addition, any content you input into the platform such as queries, prompts, or uploaded documents may be retained for service improvement, analytics, or system troubleshooting."
    },
    {
      id: "information-usage",
      title: "How We Use Your Information",
      icon: Users,
      content: "We use the information we collect to provide, maintain, and improve the OG-Fleet platform. This includes personalizing your experience, managing your account, processing payments, communicating system updates, and optimizing platform performance. Your data may also be used to ensure compliance with our Terms and Conditions, detect fraud or misuse, and meet legal or regulatory obligations. We may use anonymized or aggregated data for research, reporting, or AI model improvement, but this data will not identify you personally."
    },
    {
      id: "information-sharing",
      title: "Information Sharing",
      icon: Globe,
      content: "We do not sell or rent your personal data. However, we may share information with trusted service providers who support our platform, including payment processors, cloud hosting partners, and analytics vendors, solely for the purpose of delivering and supporting our services. If legally required, we may disclose personal information in response to lawful requests by public authorities, including to meet national security or law enforcement requirements. In the event of a business transfer, such as a merger, acquisition, or asset sale, your data may be transferred in accordance with applicable confidentiality agreements."
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: Lock,
      content: "OG-Fleet uses reasonable and industry-accepted security practices to protect your data, including encrypted communication, access controls, and secure storage systems. While we strive to protect your information, no digital transmission or storage system is completely secure, and we cannot guarantee absolute security."
    }
  ];

  const additionalInfo = [
    {
      title: "Your Rights",
      content: "You have the right to access, update, or delete your personal data. You may also opt out of non-essential communications or request that your account be permanently deleted, subject to certain retention obligations. To exercise these rights, you may contact us at the email address provided below. You can manage cookies and tracking technologies through your browser settings."
    },
    {
      title: "Age Restrictions",
      content: "OG-Fleet is not designed for use by individuals under the age of 18, and we do not knowingly collect personal data from minors. If we become aware that a minor has provided us with personal information, we will take appropriate steps to delete it."
    },
    {
      title: "International Data Transfers",
      content: "If you are accessing OG-Fleet from outside the jurisdiction in which we host or operate, you understand and agree that your data may be transferred to and processed in countries that may have different data protection laws than your country of residence."
    },
    {
      title: "Policy Updates",
      content: "We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform functionality. When we make material changes, we will notify you via email or through a notice on the platform. Your continued use of OG-Fleet after any such update constitutes your acceptance of the revised policy."
    }
  ];

  const termsContent = [
    {
      title: "User Eligibility",
      content: "OG-Fleet is intended for use by individuals or members of corporate companies who are at least 18 years old and who have the legal capacity to enter into binding contracts. The platform is designed for professional or academic purposes within the oil and gas and geoscience domains."
    },
    {
      title: "Account Responsibilities",
      content: "By creating an account, you agree to provide accurate, complete, and up-to-date information. You are solely responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account. If you suspect any unauthorized use of your account, you must notify us immediately. We reserve the right to suspend or terminate any account that violates these Terms."
    },
    {
      title: "Platform Services",
      content: "OG-Fleet provides AI-driven assistance to professionals, including text-based generative AI tools for technical queries, document creation, and data summarization. Future enhancements will include virtual reality (VR) digital twins for simulation and evaluation of drilling, production, and reservoir engineering projects. The platform is not a substitute for professional engineering judgment or regulatory compliance. Any content generated by the AI must be carefully reviewed and verified by qualified professionals before application in real-world scenarios."
    },
    {
      title: "Prohibited Uses",
      content: "Users must not use OG-Fleet for any illegal, unethical, or unauthorized purposes. You are prohibited from uploading or introducing harmful code, attempting to reverse engineer the system, or interfering with the platform's performance. Additionally, users must not utilize generated content to spread misinformation, plagiarize, or engage in deceptive practices."
    },
    {
      title: "Intellectual Property",
      content: "All intellectual property rights related to the platform including its design, code, and features remain the sole property of OG-Fleet. While users retain ownership of their uploaded content, the Company retains full rights to the platform infrastructure. AI-generated content is provided solely for non-commercial, personal, or professional use unless otherwise agreed upon in writing."
    },
    {
      title: "Payment Terms",
      content: "If you use any paid services on OG-Fleet, you agree to comply with the applicable pricing and billing terms in effect at the time of the transaction. Payments will be processed through third-party providers, and we do not store your payment details. You are responsible for managing your subscription, including renewals or cancellations. Refunds are not provided unless explicitly stated in a separate refund policy or as required by law."
    },
    {
      title: "Disclaimers",
      content: "The content produced by OG-Fleet's AI tools is generated algorithmically and may contain factual inaccuracies, technical errors, or misleading interpretations. The platform does not offer legally binding or certified engineering advice. Users must apply their own professional judgment and accept full responsibility for any decisions, interpretations, or actions based on the AI output."
    },
    {
      title: "Limitation of Liability",
      content: "To the fullest extent permitted by applicable law, OG-Fleet is provided 'as is' and 'as available' without warranties of any kind. We disclaim any liability for indirect, incidental, or consequential damages arising from your use of the platform. In any case, our total liability is limited to the amount paid by you to us in the twelve months preceding the claim, if any."
    },
    {
      title: "Data Privacy and Consent",
      content: "We take data privacy seriously. By using OG-Fleet, you consent to our practices regarding the collection, processing, and storage of your data, as described in our Privacy Policy (published at og-gpt.com/privacy-policy)."
    },
    {
      title: "Modification of Terms",
      content: "We reserve the right to modify these Terms at any time. Updates will be communicated to users through the platform or via email. Continued use of OG-Fleet following such updates constitutes your acceptance of the revised Terms."
    },
    {
      title: "Termination and Suspension",
      content: "We may suspend or permanently terminate your access to the platform if you breach these Terms, engage in fraudulent activities, or behave in a manner harmful to the platform, its users, or its reputation."
    }
  ];

  const refundSections = [
    {
      title: "Digital Nature of Services",
      content: "All services offered through OG-Fleet, including AI-generated outputs, credits, subscriptions, and virtual access to the platform, are delivered digitally and are non-tangible. As such, they are not eligible for return once delivered or accessed."
    },
    {
      title: "No Refunds for Used Services",
      content: "Refunds are not available for services that have already been accessed, used, or consumed. This includes, but is not limited to, the generation of AI responses, document processing, or usage of platform features."
    },
    {
      title: "Subscription Refunds",
      content: "There is no refund on once subscription is purchased and account is under usage. However, any extra charges or change of plan is charged that will be refunded after proper background checks. Automatic subscription renewals are non-refundable. To avoid future charges, users must cancel their subscription before the next billing cycle through their account settings."
    },
    {
      title: "Duplicate Charges or Billing Errors",
      content: "If you believe you were charged in error, or if a duplicate payment was processed, please contact us at admin@og-gpt.com within 7 calendar days of the transaction. Upon verification, we will issue a refund or credit the account as appropriate."
    },
    {
      title: "Service Interruptions and Downtime",
      content: "While we strive to maintain uninterrupted access to our platform, occasional downtime or service limitations may occur. Refunds are not issued for temporary interruptions unless the outage exceeds 72 consecutive hours and prevents all access to paid features."
    },
    {
      title: "Promotional and Trial Plans",
      content: "Discounted services, promotional subscriptions, or free trials converted to paid plans are non-refundable. It is the user's responsibility to cancel the subscription before the trial ends to avoid charges."
    },
    {
      title: "Refund Processing Time",
      content: "If a refund is approved, it will be processed through the original method of payment within 7 to 10 business days, depending on your bank or payment provider."
    },
    {
      title: "Right to Refuse Refunds",
      content: "OG-Fleet reserves the right to deny refund requests in cases of abuse, fraud, or violations of our Terms and Conditions or Acceptable Use Policy."
    }
  ];

  const servicesSections = [
    {
      title: "Platform Overview",
      content: "OG-Fleet is a digital platform offering AI-powered assistance tailored to the needs of professionals in the oil and gas, energy, and geoscience industries. The current scope of services includes access to generative AI tools for technical support, document generation, data summarization, and specialized Q&A relevant to petroleum engineering, drilling operations, production analysis, and subsurface geoscience."
    },
    {
      title: "Service Availability",
      content: "The platform is available on an 'as is' and 'as available' basis. While OG-Fleet strives to provide high-quality, uninterrupted service, there may be periods of downtime, system maintenance, or feature updates that limit access. In such cases, the Company will take reasonable steps to restore access promptly but shall not be held liable for any loss resulting from temporary unavailability or errors in service output."
    },
    {
      title: "Professional Disclaimer",
      content: "All services provided through OG-Fleet are designed to assist professionals in their daily workflows, technical decision-making, and knowledge management. However, the content and recommendations generated by the platform are created using artificial intelligence and do not constitute certified professional, legal, or engineering advice. Users are responsible for reviewing and validating all outputs independently and must exercise professional judgment before relying on them for operational or regulatory decisions."
    },
    {
      title: "Future Services",
      content: "Future services may include advanced simulation tools, VR-enabled digital twin modules, and integrated dashboards for reservoir, production, and drilling workflows. These features will be launched in phases, and access may be limited based on subscription level, technical requirements, or user qualifications."
    },
    {
      title: "Usage Restrictions",
      content: "Users must not misuse the platform, overburden its infrastructure, or attempt to interfere with its performance. Any attempt to reverse-engineer, scrape, or extract proprietary systems, or to use OG-Fleet's tools for harmful or unauthorized purposes, is strictly prohibited and may result in account termination and legal action."
    },
    {
      title: "Service Modifications",
      content: "OG-Fleet reserves the right to modify, discontinue, or enhance any aspect of its services at its sole discretion. When substantial changes are made to the scope or availability of services, users will be notified through the platform or via email. Continued use of OG-Fleet following such changes constitutes acceptance of the updated service terms."
    }
  ];

  const navigationTabs = [
    { id: "privacy", label: "Privacy Policy", icon: Shield },
    { id: "terms", label: "Terms & Conditions", icon: FileText },
    { id: "refund", label: "Refund Policy", icon: CreditCard },
    { id: "services", label: "Services Policy", icon: Settings }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "privacy":
        return (
          <>
            <div className="space-y-8 mb-12">
              {privacySections.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <div
                    key={section.id}
                    className={`group rounded-2xl p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] border ${
                      isDarkTheme 
                        ? 'bg-gray-900/50 border-gray-800 text-white hover:bg-gray-800/50' 
                        : 'bg-white border-gray-200 text-black hover:bg-amber-50'
                    } shadow-xl`}
                  >
                    <div className="flex items-start gap-6">
                      <div className={`flex-shrink-0 p-3 rounded-xl transition-colors duration-300 ${
                        isDarkTheme 
                          ? 'bg-amber-400/20 text-amber-400 group-hover:bg-amber-400/30' 
                          : 'bg-amber-100 text-amber-600 group-hover:bg-amber-200'
                      }`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h2 className={`text-xl md:text-2xl font-bold mb-4 ${
                          isDarkTheme 
                            ? 'text-amber-400' 
                            : 'text-amber-600'
                        }`}>
                          {section.title}
                        </h2>
                        <p className={`leading-relaxed ${
                          isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {section.content}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid gap-6 md:grid-cols-2 mb-12">
              {additionalInfo.map((info, index) => (
                <div
                  key={index}
                  className={`rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] border ${
                    isDarkTheme 
                      ? 'bg-gray-900/30 border-gray-800/30 hover:border-gray-700/50 text-white' 
                      : 'bg-white/60 border-gray-200 hover:border-gray-300 text-black shadow-sm hover:shadow-md'
                  }`}
                >
                  <h3 className={`text-lg font-bold mb-3 ${
                    isDarkTheme 
                      ? 'text-amber-400' 
                      : 'text-amber-600'
                  }`}>
                    {info.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${
                    isDarkTheme ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {info.content}
                  </p>
                </div>
              ))}
            </div>
          </>
        );
      
      case "terms":
        return (
          <div className="space-y-6">
            {termsContent.map((section, index) => (
              <div
                key={index}
                className={`rounded-xl p-6 border ${
                  isDarkTheme 
                    ? 'bg-gray-900/50 border-gray-800 text-white' 
                    : 'bg-white border-gray-200 text-black'
                } shadow-lg`}
              >
                <h3 className={`text-lg font-bold mb-3 ${
                  isDarkTheme ? 'text-amber-400' : 'text-amber-600'
                }`}>
                  {section.title}
                </h3>
                <p className={`leading-relaxed ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        );
      
      case "refund":
        return (
          <>
            <div className={`rounded-xl p-6 mb-8 border ${
              isDarkTheme 
                ? 'bg-gray-900/50 border-gray-800 text-white' 
                : 'bg-white border-gray-200 text-black'
            } shadow-lg`}>
              <p className={`leading-relaxed ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-700'
              }`}>
                At OG-Fleet, we are committed to providing high-quality AI services for oil and gas professionals and geoscience specialists. This Refund & Return Policy outlines the terms under which users may request a refund for services purchased through our platform. By making a purchase on our website, you acknowledge and agree to the following terms:
              </p>
            </div>
            
            <div className="space-y-6">
              {refundSections.map((section, index) => (
                <div
                  key={index}
                  className={`rounded-xl p-6 border ${
                    isDarkTheme 
                      ? 'bg-gray-900/50 border-gray-800 text-white' 
                      : 'bg-white border-gray-200 text-black'
                  } shadow-lg`}
                >
                  <h3 className={`text-lg font-bold mb-3 ${
                    isDarkTheme ? 'text-amber-400' : 'text-amber-600'
                  }`}>
                    {section.title}
                  </h3>
                  <p className={`leading-relaxed ${
                    isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </>
        );
      
      case "services":
        return (
          <div className="space-y-6">
            {servicesSections.map((section, index) => (
              <div
                key={index}
                className={`rounded-xl p-6 border ${
                  isDarkTheme 
                    ? 'bg-gray-900/50 border-gray-800 text-white' 
                    : 'bg-white border-gray-200 text-black'
                } shadow-lg`}
              >
                <h3 className={`text-lg font-bold mb-3 ${
                  isDarkTheme ? 'text-amber-400' : 'text-amber-600'
                }`}>
                  {section.title}
                </h3>
                <p className={`leading-relaxed ${
                  isDarkTheme ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {section.content}
                </p>
              </div>
            ))}
            
            <div className={`rounded-xl p-6 mt-8 border ${
              isDarkTheme 
                ? 'bg-gray-900/30 border-gray-800/50 text-white' 
                : 'bg-amber-50 border-amber-200 text-black'
            }`}>
              <p className={`text-center ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-700'
              }`}>
                For support, service inquiries, or technical issues, users may contact our help desk via email at{' '}
                <a 
                  href="mailto:admin@og-gpt.com"
                  className={`underline ${
                    isDarkTheme ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-800'
                  }`}
                >
                  admin@og-gpt.com
                </a>
                . OG-Fleet aims to provide a reliable, innovative, and evolving set of tools that empower professionals in the upstream and subsurface energy sectors.
              </p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const getSectionTitle = () => {
    const section = navigationTabs.find(tab => tab.id === activeSection);
    return section ? section.label : "Privacy Policy";
  };

  const getSectionIcon = () => {
    const section = navigationTabs.find(tab => tab.id === activeSection);
    return section ? section.icon : Shield;
  };

  const SectionIcon = getSectionIcon();

  return (
    <div className={`min-h-screen ${isDarkTheme ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Header Section */}
      <section
        className={`py-20 px-4 ${
          isDarkTheme 
            ? 'bg-gradient-to-r from-black/90 to-black/80' 
            : 'bg-gradient-to-l from-amber-300/60 to-amber-500'
        }`}
      >
        {/* Floating Elements */}
        <div className={`absolute right-0 top-0 w-32 h-32 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-pulse`} />
        <div className={`absolute left-0 bottom-0 w-40 h-40 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl animate-pulse`} style={{ animationDelay: '1s' }} />

        <div className="max-w-7xl mx-auto relative">
          {/* Back Button */}
          <button
            onClick={handleBackClick}
            className={`inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkTheme 
                ? 'text-gray-300 hover:text-amber-400 hover:bg-gray-800/50' 
                : 'text-white/80 hover:text-white hover:bg-white/20'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className={`p-4 rounded-xl ${
                isDarkTheme ? 'bg-amber-400/20 text-amber-400' : 'bg-white/20 text-white'
              }`}>
                <SectionIcon className="w-12 h-12" />
              </div>
            </div>
            <h1 className={`text-4xl lg:text-5xl font-bold mb-6 ${
              isDarkTheme 
                ? 'text-amber-400' 
                : 'text-white'
            }`}>
              {getSectionTitle()}
            </h1>
            <p className={`text-xl max-w-2xl mx-auto ${
              isDarkTheme 
                ? 'text-gray-300' 
                : 'text-white/90'
            }`}>
              {activeSection === "privacy" && "Your privacy matters to us. Learn how we protect and handle your data."}
              {activeSection === "terms" && "The terms and conditions governing your use of OG-Fleet platform."}
              {activeSection === "refund" && "Our refund and return policy for digital services and subscriptions."}
              {activeSection === "services" && "Comprehensive overview of the services and features we provide."}
            </p>
            <p className={`mt-4 ${
              isDarkTheme ? 'text-gray-400' : 'text-white/70'
            }`}>
              Last updated: August 2025
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className={`py-8 px-4 border-b ${
        isDarkTheme 
          ? 'bg-black border-gray-800' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {navigationTabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                    activeSection === tab.id
                      ? isDarkTheme
                        ? 'bg-amber-400/20 text-amber-400 border border-amber-400/30'
                        : 'bg-amber-100 text-amber-700 border border-amber-300'
                      : isDarkTheme
                        ? 'text-gray-400 hover:text-amber-400 hover:bg-gray-800/50'
                        : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-20 px-4 ${isDarkTheme ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto">
          {renderContent()}

          {/* Contact Information */}
          <div className={`rounded-2xl p-6 md:p-8 text-center border mt-12 ${
            isDarkTheme 
              ? 'bg-gray-900/50 border-gray-800 text-white' 
              : 'bg-white border-gray-200 text-black'
          } shadow-xl`}>
            <h2 className={`text-3xl font-bold mb-8 ${
              isDarkTheme 
                ? 'text-amber-400' 
                : 'text-amber-600'
            }`}>
              Need Help or Have Questions?
            </h2>
            <p className={`mb-6 max-w-2xl mx-auto ${
              isDarkTheme ? 'text-gray-300' : 'text-gray-600'
            }`}>
              If you have questions or concerns about our policies or services, 
              please don't hesitate to contact us through any of the following methods:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:admin@og-gpt.com"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-200 ${
                  isDarkTheme 
                    ? 'bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white' 
                    : 'bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white'
                }`}
              >
                <Mail className="w-4 h-4" />
                admin@og-gpt.com
              </a>
              <a
                href="https://wa.me/923342046861"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-200 ${
                  isDarkTheme 
                    ? 'bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white' 
                    : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white'
                }`}
              >
                <Phone className="w-4 h-4" />
                +92-334-204-6861
              </a>
            </div>
            <p className={`mt-4 text-sm ${
              isDarkTheme ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Visit us at{' '}
              <a 
                href="https://og-gpt.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`underline ${
                  isDarkTheme ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-800'
                }`}
              >
                og-gpt.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;